import { IDropdownOption, MessageBarType } from '@fluentui/react';
import { isEqual, snakeCase } from 'lodash';

import {
  App,
  App_Visibility,
  AppInstance,
  AppInstance_Status,
  AppInstance_Visibility,
  TagAssignment,
} from '@src/gen/ai/h2o/cloud/appstore/v1/ai.h2o.cloud.appstore';
import { Engine } from '@src/gen/ai/h2o/engine/v1/engine_pb';
import { type BytesString, bytesStringToUint8Array, toBytesString } from '@src/gen/runtime';
import { DATETIME_12H_COMMA, formatDateInBrowserTimezone, IconName, IMessageBarMessage } from '@shared-legacy/ui-kit';
import { AppCategory } from '@features-legacy/appstore/pages/AppStorePage/AppStorePage.models';
import { OTHER_CATEGORY } from '@shared/models';

export enum InstancePauseResumeOpEnum {
  Pause = 'pause',
  Postpone = 'postpone',
  Resume = 'resume',
}

export type InstancePauseResumeOp = {
  suspend: boolean;
  description: string;
  upperDescription: string;
  completedDescription: string;
  openInstance: boolean;
};

export const instanceButtonTestIdSuffixes = {
  pause: '--pause-instance-button',
  resume: '--resume-instance-button',
  visit: '--visit-instance-button',
  viewLogs: '--view-instance-logs-button',
  edit: '--edit-instance-button',
  terminate: '--terminate-instance-button',
  postpone: '--postpone-instance-button',
  details: '--app-details-instance-button',
  unavailable: '--unavailable-instance-button',
  inprogress: '--in-progess-instance-button',
};

export const getInstanceButtonTestId = (instance: AppInstance, suffix: string) =>
  `${instance?.appDetails?.name || 'app-name-unavailable'}${suffix}`;

export const engineButtonTestIdSuffixes = {
  pause: '--pause-engine-button',
  resume: '--resume-engine-button',
  visit: '--visit-engine-button',
};

export const getEngineButtonTestId = (engine: Engine, suffix: string) =>
  `${engine?.displayName || 'engine-name-unavailable'}${suffix}`;

export const InstancePauseResumeMap = new Map<InstancePauseResumeOpEnum, InstancePauseResumeOp>([
  [
    InstancePauseResumeOpEnum.Pause,
    {
      suspend: true,
      description: 'pause',
      upperDescription: 'Pause',
      completedDescription: 'Paused',
      openInstance: false,
    },
  ],
  [
    InstancePauseResumeOpEnum.Resume,
    {
      suspend: false,
      description: 'resume',
      upperDescription: 'Resume',
      completedDescription: 'Resumed',
      openInstance: true,
    },
  ],
  [
    InstancePauseResumeOpEnum.Postpone,
    {
      suspend: false,
      description: 'postpone auto-pause for',
      upperDescription: 'Postpone Auto-Pause for',
      completedDescription: 'Postponed Auto-Pause for',
      openInstance: false,
    },
  ],
]);

export const CANCEL_WEBSOCKET_DOWNLOAD = 'CANCEL_WEBSOCKET_DOWNLOAD';
const UNKNOWN = `unknown`;

const envURL = import.meta.env.VITE_API_PATH || '';

export async function fetchAppStoreVersion() {
  const response = await fetch(`${envURL}/d/version`);
  const parsedResponse = await response.text(); // this endpoint returns a ReadableStream, not JSON
  return parsedResponse;
}

const ERROR_MESSAGE_KEYS = ['message', 'reason', 'detail', 'error', 'msg'] as const;

const isFetchResponse = (error: unknown): error is Response =>
  typeof Response !== 'undefined' && error instanceof Response;

// Parses a JSON object literal (e.g. a raw `{"detail": "..."}` error body) so its message can be extracted.
const parseJsonObject = (text: string): Record<string, unknown> | undefined => {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{')) return undefined;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : undefined;
  } catch {
    return undefined;
  }
};

// Digs the human-readable message out of the error shapes the UI meets: Error instances,
// gRPC-gateway `{code, message}`, scorer `{detail}` / `{reason}`, OpenAI-style `{error: {message}}`,
// raw JSON strings, and fetch `Response` objects (which would otherwise render as `[object Response]`).
const extractErrorMessage = (error: unknown, depth = 0): string | undefined => {
  if (error === null || error === undefined || depth > 3) return undefined;
  if (typeof error === 'string') {
    const parsed = parseJsonObject(error);
    return (parsed && extractErrorMessage(parsed, depth + 1)) || error;
  }
  if (typeof error !== 'object') return String(error);
  if (isFetchResponse(error)) return error.statusText || 'Request failed';
  for (const key of ERROR_MESSAGE_KEYS) {
    const message = extractErrorMessage((error as Record<string, unknown>)[key], depth + 1);
    if (message) return message;
  }
  return undefined;
};

// Last resort when no message could be extracted; avoids `undefined` / `[object Object]` in toasts.
const stringifyUnknownError = (error: unknown): string => {
  if (error === null || error === undefined || error === '') return 'Unknown error';
  if (error instanceof Error) return error.name;
  if (typeof error !== 'object') return String(error);
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

export const handleErrMsg = (msg: string | any) => {
    if (typeof msg !== `string`) return `Unknown error`;
    // Reverse proxy error.
    if (msg === 'Unexpected token < in JSON at position 0') return 'Unavailable';
    return msg;
  },
  formatError = (error: unknown) => {
    const errorCode = isFetchResponse(error) ? error.status : (error as { code?: unknown } | null | undefined)?.code;
    const errorMessage = extractErrorMessage(error) || stringifyUnknownError(error);
    return errorCode ? `(${errorCode}) ${errorMessage}` : errorMessage;
  },
  appVisibilityMap: Record<App_Visibility, string> = {
    [App_Visibility.ALL_USERS]: 'All Users',
    [App_Visibility.PRIVATE]: 'Private',
    [App_Visibility.PUBLIC]: 'Public',
    [App_Visibility.VISIBILITY_UNSPECIFIED]: 'Unspecified',
  },
  instanceVisibilityMap: { [key in AppInstance_Visibility]: string } = {
    [AppInstance_Visibility.ALL_USERS]: 'All Users',
    [AppInstance_Visibility.PRIVATE]: 'Private',
    [AppInstance_Visibility.VISIBILITY_UNSPECIFIED]: 'Unspecified',
    [AppInstance_Visibility.PUBLIC]: 'Public',
  },
  instanceVisibilityIconMap: { [key in AppInstance_Visibility]: string } = {
    [AppInstance_Visibility.ALL_USERS]: IconName.Group,
    [AppInstance_Visibility.PRIVATE]: IconName.Lock,
    [AppInstance_Visibility.VISIBILITY_UNSPECIFIED]: IconName.Unknown,
    [AppInstance_Visibility.PUBLIC]: IconName.Unknown,
  },
  instanceStatusMap: Record<AppInstance_Status, string> = {
    [AppInstance_Status.DEPLOYED]: 'Deployed',
    [AppInstance_Status.FAILED]: 'Failed',
    [AppInstance_Status.PENDING]: 'Pending',
    [AppInstance_Status.STATUS_UNKNOWN]: 'Unknown',
    [AppInstance_Status.STATUS_UNSPECIFIED]: 'Unspecified',
    [AppInstance_Status.SUSPENDED]: 'Paused',
  },
  appVisibilityIconMap: Record<App_Visibility, string> = {
    [App_Visibility.ALL_USERS]: IconName.Group,
    [App_Visibility.PRIVATE]: IconName.Lock,
    [App_Visibility.PUBLIC]: IconName.Unknown,
    [App_Visibility.VISIBILITY_UNSPECIFIED]: IconName.Unknown,
  },
  visibilityOptions: IDropdownOption[] = Object.keys(appVisibilityMap).map((key: string) => ({
    key,
    text: appVisibilityMap[key as App_Visibility],
  })),
  specifiedVisibilityOptions: IDropdownOption[] = visibilityOptions.filter(
    (option) => option.key !== App_Visibility.VISIBILITY_UNSPECIFIED
  ),
  remapUnspecified = (str: string) => (str === 'Unspecified' ? 'Other' : str),
  isCategoryTag = (tag: Pick<TagAssignment, `isCategory` | `hidden`>) => tag.isCategory && !tag.hidden,
  categoriesFromApp = (app: App) =>
    app.tags
      ?.filter(isCategoryTag)
      .map(({ name }) => name)
      .join(', ') || OTHER_CATEGORY,
  categoryTitleFromName = (name: string, categories: AppCategory[]): string => {
    const cat = categories!.find((cat) => cat?.name === name);
    return cat?.title || name || '';
  },
  getInstanceAppTitleVersion = (appInstance: AppInstance | undefined | null) => {
    const appDetails = appInstance?.appDetails;
    return `${appDetails?.title || UNKNOWN} v${appDetails?.version || UNKNOWN}`;
  },
  redirect = (href: string, sameTab = false) => {
    const safeHref = getSafeExternalHref(href);
    if (!safeHref) {
      return;
    }
    const link = document.createElement('a');
    link.href = safeHref;
    if (!sameTab) link.target = '_blank';
    link.click();
    link.remove();
  },
  matchesSearchString = (values: string[], searchString: string): boolean => {
    return values.some((v) => v?.toLowerCase().includes(searchString.toLowerCase()));
  },
  getToastMessage = (
    message = '',
    groupId = '',
    messageBarType: MessageBarType = MessageBarType.error
  ): IMessageBarMessage => {
    return {
      groupId,
      messageBarType,
      message,
    };
  },
  getToastErrorMessage = (message = '', groupId = ''): IMessageBarMessage =>
    getToastMessage(message, groupId, MessageBarType.error),
  getAppIconImagePath = (fileName: string) => (fileName ? `/v1/asset/${fileName}` : '/logo512.png'),
  getUriHost = (uri: string): string => {
    if (!uri.startsWith('http://') && !uri.startsWith('https://')) {
      if (uri.startsWith('//')) {
        return uri.slice(2);
      }
      return uri;
    }
    let url: URL | undefined;
    try {
      url = new URL(uri);
    } catch (_error: any) {
      return uri;
    }
    if (url) {
      return url.host;
    } else {
      return uri;
    }
  },
  getUrlFromUri = (uri?: string): string | undefined => {
    if (!uri) {
      return;
    }
    if (uri.startsWith('http://') || uri.startsWith('https://')) {
      return uri;
    }
    if (uri.startsWith('//')) {
      return `${window.location.protocol}${uri}`;
    }
    return `${window.location.protocol}//${uri}`;
  };

const SAFE_EXTERNAL_LINK_PROTOCOLS = new Set(['http:', 'https:']);

/** Returns a normalized http(s) href, or undefined when the value is missing or unsafe for external navigation. */
export const getSafeExternalHref = (value?: string | null): string | undefined => {
  if (value == null) {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    return trimmed;
  }
  try {
    const url = new URL(trimmed);
    if (!SAFE_EXTERNAL_LINK_PROTOCOLS.has(url.protocol)) {
      return undefined;
    }
    return url.href;
  } catch {
    return undefined;
  }
};

/** Opens a new window/tab only when the URL passes {@link getSafeExternalHref}. */
export const openSafeExternalUrl = (value?: string | null, target = '_blank', windowFeatures?: string) => {
  const safeHref = getSafeExternalHref(value);
  if (!safeHref) {
    return;
  }
  window.open(safeHref, target, windowFeatures);
};

export const listIconSize = 64;

export function useFormAttributes() {
  const inputContainerProps = { styles: { root: { width: 450 } } },
    formRowHeight = 137,
    formRowProps = {
      horizontal: true,
      styles: { root: { width: 666, height: formRowHeight } },
      tokens: { childrenGap: 33 },
    },
    sliderFormRowProps = {
      horizontal: true,
      styles: { root: { width: 666, height: 77 } },
      tokens: { childrenGap: 15 },
    },
    inputRowProps = {
      horizontal: true,
      styles: { root: { width: 666, height: 77 } },
      tokens: { childrenGap: 15 },
    },
    singleRowInputRowProps = {
      horizontal: true,
      styles: { root: { width: 666, height: 40 } },
      tokens: { childrenGap: 15 },
    },
    sliderContainerProps = { styles: { root: { width: 450 } } };
  return {
    formRowHeight,
    formRowProps,
    inputContainerProps,
    inputRowProps,
    singleRowInputRowProps,
    sliderContainerProps,
    sliderFormRowProps,
  };
}

export const isNotEmpty = (value: any) => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return true;
  } else {
    return !!value;
  }
};

export const getNumericValue = (val: string | number | null | undefined): number | undefined => {
  const num = Number(val);
  if (isNaN(num)) {
    return undefined;
  }
  return num;
};

export const validateId = (id: string | undefined | null): boolean =>
  Boolean(id) && /^[a-z]([a-z0-9-]{0,61}[a-z0-9])?$/.test(id!);

type Item = {
  [key: string]: any;
};

type ResultObject = {
  [key: string]: Item;
};

export const arrayToObject = (arr: Item[], keyField: string): ResultObject => {
  return arr.reduce((acc, item) => {
    const key = item[keyField];
    if (key !== undefined) acc[key] = item;

    return acc;
  }, {} as ResultObject);
};

/**
 * Formats a future date as relative time from now.
 * Returns human-readable strings like "3 days", "2 hours", "45 minutes".
 * Returns "now" for past or current dates, empty string for undefined input.
 */
export const formatRelativeTime = (targetDate: Date | string | number | undefined | null): string => {
  if (!targetDate) return '';

  const now = new Date();
  const target = targetDate instanceof Date ? targetDate : new Date(targetDate);
  const diff = target.getTime() - now.getTime();

  if (diff <= 0) return 'now';

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days} ${days === 1 ? 'day' : 'days'}`;

  if (hours > 0) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;

  if (minutes > 0) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;

  return `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
};

// Helper for: "MM/DD/YYYY, HH:mm AM/PM"
export const formatDefaultDateAndTime = (input: Date | string | number | undefined | null) => {
  if (!input) return '';
  return formatDateInBrowserTimezone(input, { ...DATETIME_12H_COMMA, second: undefined, hour12: undefined });
};

// Helper for: "MM/DD/YYYY, HH:mm:ss AM/PM"
export const formatDefaultDateAndTimeWithSeconds = (input: Date | string | number | undefined | null) => {
  if (!input) return '';
  return formatDateInBrowserTimezone(input, { ...DATETIME_12H_COMMA });
};

// Helper for: the date part of formatDefaultDateAndTime — e.g. "07/27/2026" in en-US;
// day/month order follows the browser locale. (dateStyle: 'short' was previously used here,
// but it renders a 2-digit year — "3/19/26" — which contradicted the datetime helpers above.)
export const formatDefaultDateOnly = (input: Date | string | number | undefined | null) => {
  if (!input) return '';
  return formatDateInBrowserTimezone(input, { year: 'numeric', month: '2-digit', day: '2-digit' });
};

// Helper for: "2006-01-02T15:04:05Z07:00" (ISO 8601 / RFC3339 format with timezone offset)
export const formatISO8601WithTimezone = (input: Date | string | number | undefined | null) => {
  if (!input) return '';
  const date = input instanceof Date ? input : new Date(input);
  if (date.toString() === 'Invalid Date') return '';

  const pad = (num: number) => String(num).padStart(2, '0');

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  // Get timezone offset in minutes and convert to +/-HH:MM format
  const timezoneOffset = -date.getTimezoneOffset();
  const offsetSign = timezoneOffset >= 0 ? '+' : '-';
  const offsetHours = pad(Math.floor(Math.abs(timezoneOffset) / 60));
  const offsetMinutes = pad(Math.abs(timezoneOffset) % 60);
  const timezone = `${offsetSign}${offsetHours}:${offsetMinutes}`;

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${timezone}`;
};

export const encodeBytes = (data: string): BytesString => toBytesString(new TextEncoder().encode(data));

export const decodeBytes = (data: BytesString): string => {
  const uint8Array = bytesStringToUint8Array(data);
  return new TextDecoder().decode(uint8Array);
};

export const resolveColor = (cssVarString: string | undefined, defaultColor: string) => {
  if (!cssVarString) return defaultColor;

  const cssVarMatch = cssVarString.match(/var\((--[^,\s)]+),?\s*(#[0-9a-fA-F]{3,6})?\)/);
  if (!cssVarMatch) return defaultColor;

  const [, variable, fallback] = cssVarMatch;
  const computedColor = getComputedStyle(document.documentElement).getPropertyValue(variable).trim();

  return computedColor || fallback || defaultColor;
};

export const stripResourceDetailFromPath = (path: string): string => {
  // This matches: /<app>/workspaces/<workspaceId>/<resourceType>
  // const regex = /^([^#?]+\/workspaces\/[^/]+\/[^/]+)\/[^?#/]+(?:\/[^?#]*)*(?:[#?].*)?$/;
  const regex = /^(.*\/workspaces\/[^/]+\/[^/]+)\/[^/#?]+(?:\/[^/#?]*)*(?:[#?].*)?$/;
  const match = path.match(regex);
  return match ? match[1] : path;
};

export const fileToUInt8Array = (file: File): Promise<Uint8Array> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(new Uint8Array(reader.result));
      } else {
        reject(new Error('Failed to read file as ArrayBuffer'));
      }
    };
    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Computes a FieldMask update mask by comparing old and new values.
 * Returns a comma-separated string of snake_case dot-notation paths of changed fields.
 * Field names are converted to snake_case for gRPC compatibility.
 *
 * @param oldValue - The original object to compare from
 * @param newValue - The updated object to compare to
 * @param options - Optional configuration
 * @param options.exclude - Array of snake_case field paths to exclude from the mask (e.g., ['id', 'created_at'])
 * @param options.include - Array of snake_case field paths to include in the mask. If provided, only these paths will be considered.
 *
 * @returns A comma-separated string of changed field paths in snake_case format, that you can pass as the updateMask
 *
 * @example
 * const oldValue = { name: 'old', nested: { value: 1 } };
 * const newValue = { name: 'new', nested: { value: 1 } };
 * const mask = computeUpdateMask(oldValue, newValue);
 * // Returns: 'name'
 *
 * @example
 * const oldValue = { serviceAccountMapping: { azureClientId: 'old' } };
 * const newValue = { serviceAccountMapping: { azureClientId: 'new' } };
 * const mask = computeUpdateMask(oldValue, newValue);
 * // Returns: 'service_account_mapping.azure_client_id'
 *
 * @example
 * // Exclude certain fields from the update mask
 * const oldValue = { id: '1', name: 'old', updatedAt: '2024-01-01' };
 * const newValue = { id: '1', name: 'new', updatedAt: null };
 * const mask = computeUpdateMask(oldValue, newValue, { exclude: ['id', 'updated_at'] });
 * // Returns: 'name'
 *
 * @example
 * // Include only specific fields in the update mask
 * const oldValue = { name: 'old', email: 'old@test.com', phone: '123' };
 * const newValue = { name: 'new', email: 'new@test.com', phone: '456' };
 * const mask = computeUpdateMask(oldValue, newValue, { include: ['name', 'email'] });
 * // Returns: 'name,email'
 */
/**
 * Checks if a path matches a pattern (exact match or is a descendant).
 * @example
 * matchesPattern('foo', 'foo') → true
 * matchesPattern('foo.bar', 'foo') → true
 * matchesPattern('foo.bar.baz', 'foo') → true
 * matchesPattern('foobar', 'foo') → false
 */
const matchesPattern = (path: string, pattern: string): boolean => {
  return path === pattern || path.startsWith(pattern + '.');
};

/**
 * Determines if a path should be included based on include/exclude patterns.
 * Uses "most specific wins" strategy: the longest matching pattern determines the result.
 * If patterns have equal length, exclude wins as a safer default.
 *
 * @param path - The field path to check
 * @param include - Optional array of patterns to include (with their descendants)
 * @param exclude - Optional array of patterns to exclude (with their descendants)
 * @returns true if the path should be included, false otherwise
 *
 * @example
 * // Only include specified patterns
 * isPathIncluded('foo.bar', ['foo'], undefined) → true
 * isPathIncluded('baz', ['foo'], undefined) → false
 *
 * @example
 * // Exclude specified patterns
 * isPathIncluded('foo.bar', undefined, ['foo']) → false
 * isPathIncluded('baz', undefined, ['foo']) → true
 *
 * @example
 * // Most specific wins: narrow exclusion from broad inclusion
 * isPathIncluded('foo', ['foo'], ['foo.bar']) → true
 * isPathIncluded('foo.baz', ['foo'], ['foo.bar']) → true
 * isPathIncluded('foo.bar', ['foo'], ['foo.bar']) → false
 * isPathIncluded('foo.bar.x', ['foo'], ['foo.bar']) → false
 *
 * @example
 * // Most specific wins: narrow inclusion from broad exclusion
 * isPathIncluded('foo', ['foo.bar'], ['foo']) → false
 * isPathIncluded('foo.baz', ['foo.bar'], ['foo']) → false
 * isPathIncluded('foo.bar', ['foo.bar'], ['foo']) → true
 * isPathIncluded('foo.bar.x', ['foo.bar'], ['foo']) → true
 *
 * @example
 * // Equal specificity: exclude wins
 * isPathIncluded('foo', ['foo'], ['foo']) → false
 */
const isPathIncluded = (path: string, include?: string[], exclude?: string[]): boolean => {
  let bestIncludeLen = include ? -1 : 0; // If no include, everything included by default
  let bestExcludeLen = -1;

  // Find most specific (longest) matching include pattern
  if (include) {
    for (const pattern of include) {
      if (matchesPattern(path, pattern) && pattern.length > bestIncludeLen) {
        bestIncludeLen = pattern.length;
      }
    }
  }

  // Find most specific (longest) matching exclude pattern
  if (exclude) {
    for (const pattern of exclude) {
      if (matchesPattern(path, pattern) && pattern.length > bestExcludeLen) {
        bestExcludeLen = pattern.length;
      }
    }
  }

  // If include was specified but nothing matched, exclude the path
  if (include && bestIncludeLen === -1) {
    return false;
  }

  // If no exclude matched, include the path
  if (bestExcludeLen === -1) {
    return true;
  }

  // Both matched: more specific wins; if equal length, exclude wins
  return bestIncludeLen > bestExcludeLen;
};

/**
 * Computes a gRPC FieldMask update_mask string by comparing two objects.
 * Returns a comma-separated list of field paths (in snake_case) that have changed.
 *
 * @param oldValue - The original object
 * @param newValue - The updated object
 * @param options - Optional filtering options
 * @param options.exclude - Array of field path patterns to exclude (with their descendants)
 * @param options.include - Array of field path patterns to include (with their descendants)
 * @returns Comma-separated string of changed field paths
 *
 * @example
 * // Basic usage
 * computeUpdateMask(
 *   { id: '1', name: 'old' },
 *   { id: '1', name: 'new' }
 * ) → 'name'
 *
 * @example
 * // Exclude immutable fields
 * computeUpdateMask(
 *   { id: '1', name: 'old', createdAt: '2024-01-01' },
 *   { id: '2', name: 'new', createdAt: '2024-01-02' },
 *   { exclude: ['id', 'created_at'] }
 * ) → 'name'
 *
 * @example
 * // Exclude with prefix matching (excludes foo, foo.bar, foo.bar.baz, etc.)
 * computeUpdateMask(
 *   { foo: { bar: 1, baz: 2 }, qux: 3 },
 *   { foo: { bar: 10, baz: 20 }, qux: 30 },
 *   { exclude: ['foo'] }
 * ) → 'qux'
 *
 * @example
 * // Include only specific fields with prefix matching
 * computeUpdateMask(
 *   { foo: { bar: 1 }, baz: 2 },
 *   { foo: { bar: 10 }, baz: 20 },
 *   { include: ['foo'] }
 * ) → 'foo.bar'
 *
 * @example
 * // Narrow exclusion from broad inclusion
 * computeUpdateMask(
 *   { foo: { bar: 1, baz: 2 } },
 *   { foo: { bar: 10, baz: 20 } },
 *   { include: ['foo'], exclude: ['foo.bar'] }
 * ) → 'foo.baz'
 *
 * @example
 * // Narrow inclusion from broad exclusion
 * computeUpdateMask(
 *   { foo: { bar: { x: 1 }, baz: 2 } },
 *   { foo: { bar: { x: 10 }, baz: 20 } },
 *   { exclude: ['foo'], include: ['foo.bar'] }
 * ) → 'foo.bar.x'
 */
export const computeUpdateMask = <T extends Record<string, unknown>>(
  oldValue: T,
  newValue: T,
  { exclude, include }: { exclude?: string[]; include?: string[] } = {}
): string => {
  const paths: string[] = [];

  const collectChangedPaths = (oldVal: unknown, newVal: unknown, prefix = '') => {
    // If values are equal, no changes
    if (isEqual(oldVal, newVal)) return;

    // If either is not an object or is an array, mark the whole path as changed
    if (
      typeof oldVal !== 'object' ||
      typeof newVal !== 'object' ||
      oldVal == null ||
      newVal == null ||
      // gRPC FieldMask does not support fine-grained updates for repeated fields,
      // so we treat any array change as a change to the entire field
      Array.isArray(oldVal) ||
      Array.isArray(newVal)
    ) {
      // Apply include/exclude filtering before adding to paths
      if (isPathIncluded(prefix, include, exclude)) {
        paths.push(prefix);
      }
      return;
    }

    // Recursively check object properties
    const allKeys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)]);

    for (const key of allKeys) {
      const oldProp = (oldVal as Record<string, unknown>)[key];
      const newProp = (newVal as Record<string, unknown>)[key];
      // We need snake-case the keys for gRPC compatibility, but we want to keep the original keys when accessing the properties
      const snakeKey = snakeCase(key);
      const currentPath = prefix ? `${prefix}.${snakeKey}` : snakeKey;

      collectChangedPaths(oldProp, newProp, currentPath);
    }
  };

  collectChangedPaths(oldValue, newValue);

  return paths.join(`,`);
};
